/**
 * AnimatedCounter — counts from 0 to `value` with spring physics when scrolled into view.
 * Used for impact metrics. Server HTML carries the final value; the count-up
 * runs client-side only.
 */

'use client';

import React, { useEffect, useRef } from 'react';
import {
  useMotionValue,
  useSpring,
  useInView,
  useReducedMotion,
  motion
} from 'framer-motion';
import { counterSpring } from '@/components/marketing/motion-variants';

interface AnimatedCounterProps {
  value: number;
  suffix?: string;
  prefix?: string;
  /** Decimal places to show on the final value (default 0) */
  decimals?: number;
  className?: string;
}

export function AnimatedCounter({
  value,
  suffix = '',
  prefix = '',
  decimals = 0,
  className
}: AnimatedCounterProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const prefersReduced = useReducedMotion();
  const inView = useInView(ref, { once: true, amount: 0.5 });

  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, counterSpring);

  useEffect(() => {
    if (inView && !prefersReduced) {
      motionValue.set(value);
    } else if (prefersReduced) {
      motionValue.set(value);
    }
  }, [inView, value, motionValue, prefersReduced]);

  return (
    <motion.span
      ref={ref}
      className={className}
      // Render the formatted number as text using onUpdate
    >
      <CountDisplay
        springValue={springValue}
        value={value}
        prefersReduced={prefersReduced}
        decimals={decimals}
        prefix={prefix}
        suffix={suffix}
      />
    </motion.span>
  );
}

// Inner component to subscribe to spring value
function CountDisplay({
  springValue,
  value,
  decimals,
  prefix,
  suffix,
  prefersReduced
}: {
  springValue: ReturnType<typeof useSpring>;
  value: number;
  prefersReduced: boolean | null;
  decimals: number;
  prefix: string;
  suffix: string;
}) {
  // SSR + first client render carry the real value so crawlers, link previews
  // and no-JS readers never see "0%". The count-up starts from 0 only after
  // mount (client phase) — see LESSONS.md "framer-motion entrance variants".
  const [display, setDisplay] = React.useState(value);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional post-mount reset so the count-up starts from 0; SSR/first render must carry the real value
    if (!prefersReduced) setDisplay(0);
    const unsubscribe = springValue.on('change', (v) => {
      setDisplay(v);
    });
    return unsubscribe;
  }, [springValue, prefersReduced]);

  const formatted =
    decimals > 0 ? display.toFixed(decimals) : Math.round(display).toString();

  // Show the target value's decimal representation when close enough
  const finalFormatted =
    decimals > 0 ? value.toFixed(decimals) : value.toString();

  const shown = Math.abs(display - value) < 0.05 ? finalFormatted : formatted;

  return (
    <>
      {prefix}
      {shown}
      {suffix}
    </>
  );
}
